import { useMediaQuery } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import InputLabel from '@material-ui/core/InputLabel';
import Paper from '@material-ui/core/Paper';
import { useTheme } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { DateRangePicker, isInclusivelyBeforeDay } from 'react-dates';
import { useObservable } from 'react-use-observable';
import HelpTip from 'v2/components/Shared/HelpTip';
import { Space } from 'v2/components/Shared/Space';
import dashboardService from 'v2/services/dashboard';

import type { IPeriodSales } from 'v2/interfaces/dashboard';

import Chart from './Chart';
import useStyles from './styles';

import type { FocusedInputShape } from 'react-dates';

interface IDateChange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

type GraphKey = 'gains' | 'sales' | 'revenues';

interface ITabOption {
  title: string;
  key: GraphKey;
  isMonetary?: boolean;
  tip?: string;
}

const LineChart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const [period, setPeriod] = useState({
    start: moment(new Date()).subtract(7, 'days'),
    end: moment(new Date()),
  });
  const [graphKey, setGraphKey] = useState<GraphKey>('gains');
  const [focusedInput, setFocusedInput] = useState(null);
  const [selectedTab, setSelectedTab] = useState(2);

  const [isLoading] = useObservable(() => dashboardService.getLoadingPeriodSales(), []);
  const [periodSalesGains] = useObservable(() => dashboardService.getPeriodSalesGains(period.start, period.end), [period]);

  const handleTabChange = (_: React.ChangeEvent<{}>, value: number) => {
    const tabs = ['sales', 'revenues', 'gains'] as const;
    setSelectedTab(value);
    setGraphKey(tabs[value]);
  };

  const handleFocusChange = (arg: FocusedInputShape) => {
    setFocusedInput(arg);
  };

  const handleDateChange = ({ startDate, endDate }: IDateChange) => {
    setPeriod((period) => ({
      start: startDate || period.start,
      end: endDate || period.end,
    }));
  };

  const getTotalFromPeriod = (data: IPeriodSales, key: GraphKey) => {
    return (data[key] || []).reduce((acc, item) => acc + item.producer + item.coproducer + item.affiliate, 0);
  };

  const isOutsideRange = (day: moment.Moment) => {
    return !isInclusivelyBeforeDay(day, moment());
  };

  const chartData = useMemo(
    () =>
      periodSalesGains?.[graphKey]?.map((item) => {
        const formattedLabel = moment(item.date).format('DD/MM');

        return {
          ...item,
          date: formattedLabel,
        };
      }),
    [graphKey, periodSalesGains]
  );

  const tabOptions: ITabOption[] = [
    {
      title: 'Vendas',
      key: 'sales',
      isMonetary: false,
    },
    {
      title: 'Faturamento',
      key: 'revenues',
      isMonetary: true,
      tip: 'Este é o total do seu faturamento bruto, para isso consideramos apenas faturas com status paga, aguardando reembolso e reembolsado.',
    },
    {
      title: 'Ganhos',
      key: 'gains',
      isMonetary: true,
    },
  ];

  return (
    <Paper square={false} elevation={1} classes={{ root: classes.paperRoot }}>
      {isLoading && <Spin />}
      {!isLoading && chartData && (
        <>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant='fullWidth'
            classes={{
              indicator: classes.tabsIndicator,
            }}
          >
            {tabOptions.map((tabOption) => {
              const total = getTotalFromPeriod(periodSalesGains, tabOption.key);
              return (
                <Tab
                  key={tabOption.key}
                  label={
                    <>
                      <Typography className={classes.tabTitle}>
                        {tabOption.title}
                        {tabOption.tip && (
                          <>
                            <Space axis='horizontal' size={4} />
                            <HelpTip text={tabOption.tip} />{' '}
                          </>
                        )}
                      </Typography>
                      {tabOption.isMonetary ? <ColoredFormattedNumber format='BRL' value={total} /> : <Typography>{total}</Typography>}
                    </>
                  }
                  classes={{
                    root: classes.tabRoot,
                    selected: classes.tabSelected,
                    wrapper: classes.tabWrapper,
                  }}
                />
              );
            })}
          </Tabs>
          <Divider />
          <div className={classes.dateContainer}>
            <InputLabel htmlFor='start_date_period_sales_gains'>Data de pagamento:</InputLabel>
            <DateRangePicker
              startDate={period.start}
              startDateId='start_date_period_sales_gains'
              endDate={period.end}
              endDateId='end_date_period_sales_gains'
              onDatesChange={handleDateChange}
              focusedInput={focusedInput}
              onFocusChange={handleFocusChange}
              displayFormat='DD/MM/YYYY'
              small
              hideKeyboardShortcutsPanel
              startDatePlaceholderText='Data inicial'
              endDatePlaceholderText='Data final'
              isOutsideRange={isOutsideRange}
              minimumNights={0}
              orientation='horizontal'
              calendarInfoPosition='top'
              numberOfMonths={isMobile ? 1 : 2}
              enableOutsideDays={false}
            />
          </div>
          <Divider />
          <Chart data={chartData} totalGains={getTotalFromPeriod(periodSalesGains, 'gains')} />
        </>
      )}
    </Paper>
  );
};

export default LineChart;
